import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import ResponsibleSelect from './ResponsibleSelect';

/**
 * FinalMeasureModal - Full-form editor for one PTR Final row.
 * Same fields as the Atelier 5 measure modal, adapted to fusion finals:
 * sources are read-only (derived), no scenario picker, free-text owner
 * (a fusion spans several analyses), no add-new (rows are derived).
 * Values are persisted by the caller as final-measure overrides.
 */
const FinalMeasureModal = ({ isOpen, onClose, measure = null, onSave, responsibleOptions = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'protection',
    responsiblePerson: '',
    budget: '',
    estimatedEffort: 0,
    deadline: '',
    priority: 'medium',
    justification: '',
    businessValueName: '',
  });

  const pMap = ['', 'low', 'medium', 'high', 'critical'];
  const pLabels = ['—', 'P4', 'P3', 'P2', 'P1'];
  const pColors = ['', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];

  useEffect(() => {
    if (measure) {
      setFormData({
        name: measure.name || measure.title || measure.measureName || '',
        description: measure.description || '',
        category: measure.category || 'protection',
        responsiblePerson: measure.responsiblePerson || measure.responsibleParty || '',
        budget: measure.budget ?? '',
        estimatedEffort: Number(measure.estimatedEffort) || 0,
        deadline: measure.deadline || '',
        priority: measure.priority || 'medium',
        justification: measure.justification || '',
        businessValueName: measure.businessValueName || '',
      });
    }
  }, [measure, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave({
      ...formData,
      budget: Number(formData.budget) || 0,
      estimatedEffort: Number(formData.estimatedEffort) || 0,
      businessValueName: String(formData.businessValueName || ''),
    });
    onClose();
  };

  const prioIdx = pMap.indexOf(formData.priority);
  const prioCur = prioIdx >= 0 ? prioIdx : 0;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-blue-50/40 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">
            {t('fusionPtr.editFinalMeasure', 'Modifier la mesure consolidée')}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('common.close', 'Fermer')} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.measure', 'Mesure de sécurité')}</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.measureCategory', 'Catégorie')}</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400">
                <option value="gouvernance et anticipation">{t('workshop5.category.governance', 'Gouvernance')}</option>
                <option value="protection">{t('workshop5.category.protection', 'Protection')}</option>
                <option value="défense">{t('workshop5.category.defense', 'Défense')}</option>
                <option value="résilience">{t('workshop5.category.resilience', 'Résilience')}</option>
                <option value="other">{t('workshop5.category.other', 'Autre')}</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.description', 'Description')}</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.responsible', 'Responsable')}</label>
              <ResponsibleSelect
                value={formData.responsiblePerson}
                onChange={(v) => setFormData({ ...formData, responsiblePerson: v })}
                options={responsibleOptions}
                selectClassName="w-full text-sm border border-slate-200 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                inputClassName="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.budget', 'Budget (€)')}</label>
              <input type="number" min="0" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" placeholder="Ex: 50000" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.estimatedEffort', 'Charge estimée (j/h)')}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-bold text-slate-700 min-w-[20px]">{Number(formData.estimatedEffort) || 0}</span>
                <button type="button" onClick={() => setFormData({ ...formData, estimatedEffort: (Number(formData.estimatedEffort) || 0) + 1 })} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">+</button>
                <button type="button" onClick={() => setFormData({ ...formData, estimatedEffort: Math.max(0, (Number(formData.estimatedEffort) || 0) - 1) })} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">-</button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.deadline', 'Échéance')}</label>
              <input type="date" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.priority', 'Priorité')}</label>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold text-white ${pColors[prioCur] || 'bg-gray-200 text-gray-500'}`}>{pLabels[prioCur]}</span>
                <button type="button" onClick={() => setFormData({ ...formData, priority: pMap[Math.min(4, prioCur + 1)] })} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">+</button>
                <button type="button" onClick={() => setFormData({ ...formData, priority: pMap[Math.max(0, prioCur - 1)] })} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">-</button>
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('fusionPtr.businessValueLabel', 'Valeur métier')}</label>
              <input type="text" value={formData.businessValueName} onChange={(e) => setFormData({ ...formData, businessValueName: e.target.value })} placeholder={t('fusionPtr.businessValuePlaceholder', 'Valeur(s) métier, séparées par des virgules')} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">{t('fusionPtr.justificationLabel', 'Justification')}</label>
              <textarea value={formData.justification} onChange={(e) => setFormData({ ...formData, justification: e.target.value })} rows={2} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </div>
          </div>
          {(measure?.sources?.length > 0) && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              {t('fusionPtr.finalModalSources', 'Sources : {{sources}}', {
                sources: measure.sources.map((s) => `${s.name || ''} (${s.analysisName || ''})`).join(' + '),
              })}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">{t('common.cancel', 'Annuler')}</button>
          <button type="button" onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">{t('common.save', 'Enregistrer')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

FinalMeasureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  measure: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  responsibleOptions: PropTypes.array,
};

export default FinalMeasureModal;
